/**
 * ModalCard — the overlay + titled card every custom modal in the app draws by
 * hand (same styling as ConfirmDialog / OfferFormDialog). Portals to <body> so
 * it escapes a table's overflow/scroll context, and publishes its inner surface
 * on ModalPortalSurfaceContext so comboboxes inside it can portal THERE rather
 * than to <body> — outside the surface their clicks are swallowed by the
 * stopPropagation that keeps a backdrop click from closing the modal.
 * For Media Devoted | Created By James Alexandrov
 */

import { FC, ReactNode, useState } from "react";
import { createPortal } from "react-dom";

import {
  ModalPortalSurfaceContext,
  useModalPortalSurface,
} from "./ModalPortalSurfaceContext";
import { backdropClick } from "./backdropClick";
import { useEscapeToClose } from "./useEscapeToClose";

interface ModalCardProps {
  open: boolean;
  title: string;
  /** Optional line under the title — context the body shouldn't have to repeat. */
  subtitle?: ReactNode;
  /** Max-width class for the card (default "max-w-3xl"). */
  widthClass?: string;
  /** Blocks the backdrop click while a write is in flight. */
  busy?: boolean;
  /** Stacking order. Lower it when the modal can open a confirm ON TOP of
   *  itself, so the confirm isn't painted underneath. */
  zClass?: string;
  onClose: () => void;
  children: ReactNode;
}

export const ModalCard: FC<ModalCardProps> = ({
  open,
  title,
  subtitle,
  widthClass = "max-w-3xl",
  busy = false,
  zClass = "z-[200]",
  onClose,
  children,
}) => {
  const [surface, setSurface] = useState<HTMLDivElement | null>(null);
  // A modal nested in a Radix dialog (the drawer) must portal INTO that
  // dialog's element — a body portal sits outside its FocusScope, which then
  // reclaims focus on every keystroke and inputs become untypeable.
  const host = useModalPortalSurface();
  // Escape closes the topmost modal — the keyboard twin of the backdrop click.
  useEscapeToClose(open, busy, onClose);
  if (!open) return null;
  // pointer-events-auto: a Radix Dialog (our Drawer) sets `pointer-events: none`
  // on <body> while open, and this portals to <body> — without it the modal
  // paints but nothing inside can be clicked. A no-op outside a drawer.
  return createPortal(
    <div
      className={`pointer-events-auto fixed inset-0 ${zClass} flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm`}
      // Closes only when the press ALSO landed on the backdrop — a text
      // selection that starts in an input and ends outside must not close it.
      onClick={backdropClick(busy ? undefined : onClose)}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={setSurface}
        // min(…, 100%): inside a drawer the overlay is the drawer's box, not
        // the viewport — the card must fit it or its header and footer clip.
        className={`relative max-h-[min(90vh,100%)] w-full ${widthClass} overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="text-base font-semibold">{title}</h3>
          {subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="p-4">
          <ModalPortalSurfaceContext.Provider value={surface}>
            {children}
          </ModalPortalSurfaceContext.Provider>
        </div>
      </div>
    </div>,
    host ?? document.body,
  );
};

export default ModalCard;
